import React from "react";
import type Highcharts from "highcharts";
import type { MinMax } from "@ui/Highcharts/hooks/useAxisMinMax";
import useAxisMinMax from "@ui/Highcharts/hooks/useAxisMinMax";
import isFuncState from "../../../../lib/isFuncState";
import useAnalysisKeyboardSelect from "./useAnalysisKeyboardSelect";
import useAnalysisGroupPlotLines from "./useAnalysisGroupPlotLines";
import useGroups from "../../../../api/useGroups";
import useAnalysisSelectionPlotLines from "./useAnalysisSelectionPlotLines";
import useAnalysisMouseSelect from "./useAnalysisMouseSelect";
import usePostLatestAnalysisPlotBand from "./usePostLatestAnalysisPlotBand";
import type { Project } from "../../../../api/useProjects";
import type { Source } from "../../../../api/useSources";
import { useProjectPeriod } from "../../useProjectPeriod";
import type { Analysis } from "../../../../api/Analysis";
import type { ActiveAnalysis } from "../../../../api/useActiveAnalysesStream";
import { getLatestActiveAnalysisDate } from "../../latestActiveAnalysisDate";

const AXIS_ID = "period-axis";

const useAnalysisXAxis = ({
  chartRef,
  project,
  source,
  analyses,
  boundaries,
  postLatestAnalysisLabelTextKey,
}: {
  project: Project;
  source: Source;
  analyses: Analysis[];
  chartRef: React.RefObject<Highcharts.Chart>;
  boundaries: "active-analyses" | "analyses";
  postLatestAnalysisLabelTextKey?: "chart.period.no_analysis_yet" | "chart.period.no_data_yet";
}) => {
  const projectId = project.id;
  const latestActiveAnalysisDate = React.useMemo(
    () => getLatestActiveAnalysisDate(project),
    [project],
  );
  const [period, setPeriod, extremes] = useProjectPeriod(
    boundaries === "analyses"
      ? {
          source,
          project,
          boundaries: "analyses",
        }
      : {
          source,
          project,
          analyses: analyses as ActiveAnalysis[],
          boundaries: "active-analyses",
        },
  );

  useAnalysisGroupPlotLines({
    chartRef,
    groups: useGroups(projectId),
    period,
    setPeriod,
  });

  useAnalysisSelectionPlotLines({
    chartRef,
    boundaries,
    analyses,
    projectId,
    period,
    timeZone: project.timezone,
  });
  useAnalysisKeyboardSelect({ chartRef, analyses, projectId });
  useAnalysisMouseSelect({ projectId, analyses, chartRef });
  usePostLatestAnalysisPlotBand({
    chartRef,
    analyses: boundaries === "analyses" ? analyses : undefined,
    latestDate: boundaries === "analyses" ? undefined : latestActiveAnalysisDate,
    labelTextKey: postLatestAnalysisLabelTextKey,
  });

  const minMax = React.useMemo(() => ({ min: period.from, max: period.to }), [period]);
  const setMinMax = React.useCallback(
    (value: React.SetStateAction<MinMax>) => {
      if (isFuncState(value)) {
        setPeriod(oldPeriod => {
          const newPeriod = value({ min: oldPeriod.from, max: oldPeriod.to });
          return { from: Number(newPeriod.min), to: Number(newPeriod.max) };
        });
      } else {
        setPeriod({ from: Number(value.min), to: Number(value.max) });
      }
    },
    [setPeriod],
  );

  const initialMinMax = useAxisMinMax({
    chartRef,
    minMax,
    setMinMax,
    axisId: AXIS_ID,
  });

  return React.useMemo(
    () =>
      ({
        ...initialMinMax,
        min: extremes.from,
        id: AXIS_ID,
        type: "datetime" as const,
        tickPixelInterval: 150, // default 100 is too dense for 'DD/MM/YYYY'
      }) as const,
    [initialMinMax, extremes],
  );
};
export default useAnalysisXAxis;
